import { rootVariantKeys, stripHarakat, normalizeArabicChar, maqsuraFallbackKey } from "./arabicNormalize";

const KEY = "search_history";
const MAX = 1000;

const stripTags = (html) => (html ? html.replace(/<[^>]*>/g, "") : "");

// History entries and bookmarks are stored under whatever key the root was opened
// with (سءل, سأل and ك😀ت😀ب all load سال), so compare them by the search keys the
// lookup resolves them to. A compound root ("جلو,جلى") keeps one key per alternate.
export const rootKey = (word) => rootVariantKeys(word).join(",") || word;

const entryWord = (entry) => (typeof entry === "string" ? entry : entry?.word);

// Which root a stored key stands for, the one rule History, Bookmarks and the word page
// all go by: its rootKey, except that a root typed in its citation form (رمى) opens the
// entry the dictionary keys with ي (رمي), so the two keys are one root; but only when
// the entry's dictionary root (`root`, stored with newer history entries and bookmarks)
// shows it: the root lists the ي key (رمي; نكي, نكى), or doesn't list the ى key, which
// then isn't one of the dictionary's (حى opens حيو / حيي). A key the dictionary has
// with ى itself names a different entry: بلى "yes" is not بلي "to wear out" (nor انى
// اني, حفى حفي, ونى وني).
export const rootIdentity = (word, root) => {
  const key = rootKey(word);
  const alt = maqsuraFallbackKey(key);
  if (!alt || !root) return key;
  const keys = rootVariantKeys(root);
  return keys.includes(alt) || !keys.includes(key) ? alt : key;
};

// One entry per root, for a newest-first list: the first entry for each root wins
// and takes its rootKey as its `word`; `keys` lists every stored word it stands
// for, so deleting it can remove them all.
export const dedupeHistory = (entries) => {
  const byKey = new Map();
  for (const entry of entries || []) {
    const raw = entryWord(entry);
    if (typeof raw !== "string") continue;
    const word = rootKey(raw);
    const group = rootIdentity(raw, typeof entry === "string" ? null : entry?.root);
    const kept = byKey.get(group);
    if (kept) {
      if (!kept.keys.includes(raw)) kept.keys.push(raw);
    } else {
      byKey.set(group, { ...(typeof entry === "string" ? {} : entry), word, keys: [raw] });
    }
  }
  return [...byKey.values()];
};

// One group per root for a newest-first list (`meta` is the sync store's bookmark
// meta). A root bookmarked under two keys (سأل and سال, or رمى and رمي) shows and opens
// its most recently added copy; `words` lists every stored key so Delete can remove them all.
export const groupBookmarks = (bookmarks, meta) => {
  const byKey = new Map();
  Object.entries(bookmarks || {}).forEach(([word, data], order) => {
    const key = rootIdentity(word, data?.[0]?.root);
    const addedAt = meta?.[word]?.addedAt || 0;
    const kept = byKey.get(key);
    if (!kept) {
      byKey.set(key, { key, word, data, addedAt, order, words: [word] });
    } else {
      kept.words.push(word);
      if (addedAt >= kept.addedAt) Object.assign(kept, { word, data, addedAt, order });
    }
  });
  // Insertion order breaks ties: bookmarks older than addedAt all share one stamp.
  return [...byKey.values()].sort((a, b) => b.addedAt - a.addedAt || b.order - a.order);
};

// A list filter's query against a root key, given the query as a search key
// (`arabicQ`). Typed in Arabic it may match anywhere in the key. Typed in Latin
// letters it matches only from the start: transliteration skips short vowels (kutub
// -> كتب), so an English word in the filter (sun -> سن) would otherwise turn up roots
// that merely contain those letters (اسن). ى and ي count as one letter: a root typed
// as رمى is kept under the dictionary's رمي, or the other way round.
const unifyYa = (text) => text.replace(/ى/g, "ي");
export const queryMatchesKey = (query, arabicQ, key) => {
  if (!arabicQ || !key) return false;
  const [q, k] = [unifyYa(arabicQ), unifyYa(key)];
  return /[؀-ۿ]/.test(query) ? k.includes(q) : k.startsWith(q);
};

// Transliterations use diacritics and ‘ ’ for ayn and hamza (fariḥa, qara’a), which a
// phone keyboard doesn't type; compare them, and the query, with those folded away.
export const foldTransliteration = (text) =>
  (text || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019\u02bb\u02bc\u02be\u02bf'`]/g, "")
    .toLowerCase();

const HAMZA_SEATS = "ءأإآئؤ";
const NON_LETTERS = /[^ء-يٱ-ۓ]+/;
// The dataset's texts write ه as ھ, and a root's final ي is often written ى.
const unifyLetter = (c) => (c === "ھ" ? "ه" : c === "ى" ? "ي" : c);

const alignToKey = (key, token) => {
  if (token.length !== key.length) return null;
  let out = "";
  for (let i = 0; i < key.length; i++) {
    const t = token[i];
    if (unifyLetter(t) === unifyLetter(key[i])) out += key[i];
    else if (HAMZA_SEATS.includes(t) && normalizeArabicChar(t) === key[i]) out += t;
    else return null;
  }
  return out;
};

// List keys are hamza-folded search keys: سأل is stored as سال (which reads as
// another word) and رأي as راي. For display, recover the dictionary's spelling
// from the entry's own Arabic (`candidates`, best first): the first word with the
// key's letters, up to hamza seats, lends the key its hamzas. Falls back to the key.
export const rootSpelling = (key, candidates) => {
  if (!key || !/[او]/.test(key)) return key;
  for (const text of candidates || []) {
    for (const token of stripHarakat(text || "").split(NON_LETTERS)) {
      const spelled = alignToKey(key, token);
      if (spelled) return spelled;
    }
  }
  return key;
};

// How each of the last 50 search keys was typed (سؤال for the key سوال), which root
// analysis needs. The word page also keeps it on its history entry, but when Android
// recreates the activity it reopens the page in a new WebView, without history state.
const TYPED_KEY = "hw-raw-queries";
const MAX_TYPED = 50;

// [key, typed] pairs, newest last.
const typedSpellings = () => {
  try {
    const pairs = JSON.parse(localStorage.getItem(TYPED_KEY));
    return Array.isArray(pairs) ? pairs.filter((pair) => Array.isArray(pair) && typeof pair[1] === "string") : [];
  } catch {
    return [];
  }
};

export const rememberTypedSpelling = (key, typed) => {
  try {
    const pairs = typedSpellings().filter(([saved]) => saved !== key);
    pairs.push([key, typed]);
    localStorage.setItem(TYPED_KEY, JSON.stringify(pairs.slice(-MAX_TYPED)));
  } catch {
    /* storage unavailable */
  }
};

export const typedSpellingOf = (key) => typedSpellings().find(([saved]) => saved === key)?.[1];

// All of them, or those of `keys`.
export const forgetTypedSpellings = (keys) => {
  try {
    if (!keys) {
      localStorage.removeItem(TYPED_KEY);
      return;
    }
    const pairs = typedSpellings();
    const kept = pairs.filter(([saved]) => !keys.includes(saved));
    if (kept.length < pairs.length) localStorage.setItem(TYPED_KEY, JSON.stringify(kept));
  } catch {
    /* storage unavailable */
  }
};

// Entry format: { word, arabic, transliteration, definition, tags, timestamp }
// Legacy entries may be plain strings (word only)

export const addToSearchHistory = (word, rootData) => {
  const history = getSearchHistory().filter((entry) => {
    const w = typeof entry === "string" ? entry : entry.word;
    return rootKey(w) !== rootKey(word);
  });

  const entry = { word: rootKey(word), timestamp: Date.now() };

  if (rootData) {
    const firstDef = rootData.definitions?.[0];
    const firstNoun = rootData.nouns?.[0];
    const firstEntry = firstDef || firstNoun;

    if (firstEntry) {
      entry.arabic = firstEntry.text || "";
      entry.transliteration = firstEntry.transliteration || "";
      entry.definition = stripTags(
        firstEntry.translation?.short || firstEntry.translation?.text || ""
      );
      entry.tags = [];
      if (firstDef) {
        entry.tags.push("Verb");
        if (firstDef.form) entry.tags.push(`G-${firstDef.form}`);
      } else {
        entry.tags.push("Noun");
      }
    }
  }

  history.unshift(entry);
  localStorage.setItem(KEY, JSON.stringify(history.slice(0, MAX)));
};

export const getSearchHistory = () => {
  try {
    return JSON.parse(localStorage.getItem(KEY)) || [];
  } catch {
    return [];
  }
};

export const removeFromHistory = (word) => {
  const history = getSearchHistory().filter((entry) => {
    const w = typeof entry === "string" ? entry : entry.word;
    return rootKey(w) !== rootKey(word);
  });
  localStorage.setItem(KEY, JSON.stringify(history));
};

export const clearSearchHistory = () => {
  localStorage.removeItem(KEY);
};
